import { CalendarDateRangeIcon, UsersIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DatePicker, TextInput } from '~/components';
import { b2bSideCopy, type ListingSide } from '../../b2bSideCopy';
import {
  useNegotiationBaseline,
  useNegotiationInputsDisabled,
  useNegotiationResponseType,
} from '../provider';
import { TermCompareSection } from '../sub-components/TermCompareSection';

const TAGS = CT.employer.negotiation;

export const ContractOverview = ({ listingSide }: { listingSide?: ListingSide | null }) => {
  const { setValue } = useFormContext();
  const inputsDisabled = useNegotiationInputsDisabled();
  const responseType = useNegotiationResponseType();
  const [
    startDate,
    endDate,
    numOfFtes,
    fteShiftsPerYear,
    totalShiftsFormatted,
    numOfShifts,
    hoursPerShift,
  ] = useWatch({
    name: [
      'startDate',
      'endDate',
      'numOfFtes',
      'fteShiftsPerYear',
      'totalShiftsFormatted',
      'numOfShifts',
      'hoursPerShift',
    ],
  });

  const negotiation = useNegotiationBaseline();

  const contractDatesChanged = useMemo(
    () => ({
      startDate: startDate !== negotiation?.startDate,
      endDate: endDate !== negotiation?.endDate,
    }),
    [startDate, endDate, negotiation?.startDate, negotiation?.endDate]
  );

  const msaasStaffingRequirementsChanged = useMemo(
    () => ({
      numOfFtes: Number(numOfFtes) !== negotiation?.numOfFtes,
      fteShiftsPerYear: Number(fteShiftsPerYear) !== negotiation?.fteShiftsPerYear,
      totalShifts: totalShiftsFormatted !== Number(fteShiftsPerYear) * Number(numOfFtes),
      hoursPerShift: Number(hoursPerShift) !== Number(negotiation?.hoursPerShift),
    }),
    [numOfFtes, fteShiftsPerYear, totalShiftsFormatted, hoursPerShift, negotiation]
  );

  const jobStaffingRequirementsChanged = useMemo(
    () => ({
      numOfShifts: Number(numOfShifts) !== Number(negotiation?.numOfShifts),
      hoursPerShift: Number(hoursPerShift) !== Number(negotiation?.hoursPerShift),
    }),
    [hoursPerShift, negotiation?.hoursPerShift, negotiation?.numOfShifts, numOfShifts]
  );

  useEffect(() => {
    if (responseType !== 'msaas') return;
    setValue('totalShiftsFormatted', Number(fteShiftsPerYear) * Number(numOfFtes));
  }, [fteShiftsPerYear, numOfFtes, responseType, setValue]);

  const baselineTotalShifts =
    negotiation?.numOfShifts ??
    Number(negotiation?.numOfFtes) * Number(negotiation?.fteShiftsPerYear);

  const staffingRequirementsChanged =
    responseType === 'job' ? jobStaffingRequirementsChanged : msaasStaffingRequirementsChanged;

  const staffingTerms =
    responseType === 'job'
      ? [
          {
            label: 'Number of Shifts',
            value: negotiation?.numOfShifts,
            newValueField: (
              <TextInput
                name="numOfShifts"
                flagged={jobStaffingRequirementsChanged.numOfShifts}
                type="number"
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.totalShifts}
              />
            ),
          },
          {
            label: 'Hours per Shift',
            value: negotiation?.hoursPerShift,
            newValueField: (
              <TextInput
                name="hoursPerShift"
                flagged={jobStaffingRequirementsChanged.hoursPerShift}
                type="number"
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.hoursPerShiftInput}
              />
            ),
          },
        ]
      : [
          {
            label: 'Number of FTEs',
            value: negotiation?.numOfFtes,
            newValueField: (
              <TextInput
                name="numOfFtes"
                flagged={msaasStaffingRequirementsChanged.numOfFtes}
                type="number"
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.fteInput}
              />
            ),
          },
          {
            label: 'Shifts per FTE/Year',
            value: negotiation?.fteShiftsPerYear,
            newValueField: (
              <TextInput
                name="fteShiftsPerYear"
                flagged={msaasStaffingRequirementsChanged.fteShiftsPerYear}
                type="number"
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.shiftsPerFTE}
              />
            ),
          },
          {
            label: 'Total Shifts (Calculated)',
            value: baselineTotalShifts,
            newValueField: (
              <TextInput
                name="totalShiftsFormatted"
                disabled
                type="number"
                flagged={msaasStaffingRequirementsChanged.totalShifts}
                cyTag={TAGS.contractForm.totalShifts}
              />
            ),
          },
          {
            label: 'Hours per Shift',
            value: negotiation?.hoursPerShift,
            newValueField: (
              <TextInput
                name="hoursPerShift"
                flagged={msaasStaffingRequirementsChanged.hoursPerShift}
                type="number"
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.hoursPerShiftInput}
              />
            ),
          },
        ];

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TermCompareSection
        icon={<CalendarDateRangeIcon className="h-5 w-5" />}
        changedTermsCount={Object.values(contractDatesChanged).filter(Boolean).length}
        title="Contract Dates"
        terms={[
          {
            label: 'Start Date',
            value: negotiation?.startDate,
            newValueField: (
              <DatePicker
                name="startDate"
                flagged={contractDatesChanged.startDate}
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.startDateInput}
              />
            ),
          },
          {
            label: 'End Date',
            value: negotiation?.endDate,
            newValueField: (
              <DatePicker
                name="endDate"
                flagged={contractDatesChanged.endDate}
                disabled={inputsDisabled}
                cyTag={TAGS.contractForm.endDateInput}
              />
            ),
          },
        ]}
      />
      <TermCompareSection
        classname=""
        icon={<UsersIcon className="h-5 w-5" />}
        changedTermsCount={Object.values(staffingRequirementsChanged).filter(Boolean).length}
        title={b2bSideCopy(listingSide).staffingHeading}
        terms={staffingTerms}
      />
    </div>
  );
};
